"use client";

import Script from "next/script";
import { analyticsConfig } from "@/utils/analytics";

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

declare global {
  interface Window {
    gtag: (
      command: string,
      targetId: string,
      config?: Record<string, string | number | boolean | object>
    ) => void;
    dataLayer: object[];
  }
}

export default function GoogleAnalytics() {
  if (!GA_MEASUREMENT_ID) {
    if (analyticsConfig.enableDebugMode) {
      console.warn("Google Analytics Measurement ID is not configured");
    }
    return null;
  }

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          
          gtag('config', '${GA_MEASUREMENT_ID}', {
            // Enhanced ecommerce and engagement tracking
            send_page_view: true,
            anonymize_ip: true,
            allow_google_signals: true,
            allow_ad_personalization_signals: false,
            
            // Custom parameters for portfolio tracking
            custom_map: {
              custom_parameter_1: 'project_id',
              custom_parameter_2: 'project_category',
              custom_parameter_3: 'device_type',
              custom_parameter_4: 'section_name',
              custom_parameter_5: 'interaction_type'
            },
            
            // Enhanced measurement features
            enhanced_measurement: {
              scrolls: true,
              outbound_clicks: true,
              site_search: false,
              video_engagement: false,
              file_downloads: true
            },
            
            // Debug mode for development
            debug_mode: ${analyticsConfig.enableDebugMode},
            
            // Performance and engagement settings
            engagement_time_msec: 100,
            
            // Privacy settings
            ads_data_redaction: true,
            url_passthrough: false,
            
            // Site-specific settings
            content_group1: 'portfolio',
            content_group2: typeof window !== 'undefined' ? 
              (window.innerWidth <= 768 ? 'mobile' : 'desktop') : 'unknown'
          });
          
          // Track initial performance metrics
          if (typeof window !== 'undefined' && window.performance) {
            gtag('event', 'page_load_start', {
              event_category: 'performance',
              event_label: document.title,
              value: Math.round(window.performance.now())
            });
          }
          
          // Set up error tracking
          window.addEventListener('error', function(e) {
            gtag('event', 'javascript_error', {
              event_category: 'performance',
              event_label: e.message,
              value: 1,
              error_message: e.message,
              error_filename: e.filename,
              error_lineno: e.lineno
            });
          });
          
          // Track unhandled promise rejections
          window.addEventListener('unhandledrejection', function(e) {
            gtag('event', 'promise_rejection', {
              event_category: 'performance',
              event_label: 'Unhandled Promise Rejection',
              value: 1,
              error_message: e.reason?.message || 'Unknown error'
            });
          });
        `}
      </Script>
    </>
  );
}